import React from "react";
import { StatusBadge } from "../design-system";
import type { AgentOsStudioReadModelView } from "../api";

export type OwnerAcceptanceStatus = "ready" | "owner_test_required" | "gap";

export interface OwnerAcceptanceJourney {
  id: string;
  label: string;
  status: OwnerAcceptanceStatus;
  section: string;
  evidence: string[];
}

function hasUnifiedLane(readModel: AgentOsStudioReadModelView, id: string): boolean {
  return (readModel.registryControl ?? []).some((row) => row.id === id && row.supportsUnifiedMutationLane);
}

function controlStatus(readModel: AgentOsStudioReadModelView, id: string): string | null {
  return (readModel.registryControl ?? []).find((row) => row.id === id)?.status ?? null;
}

export function deriveOwnerAcceptanceJourneys(readModel: AgentOsStudioReadModelView): OwnerAcceptanceJourney[] {
  const brainReady = (readModel.brainEntries?.length ?? 0) > 0;
  const brainMutable = (readModel.brainEntries ?? []).some((entry) => entry.mutationSupported === true || typeof entry.mutationBaseHash === "string");
  const skillsReady = hasUnifiedLane(readModel, "skills");
  const routinesReady = hasUnifiedLane(readModel, "routines");
  const policiesReady = hasUnifiedLane(readModel, "policies");
  const hooksStatus = controlStatus(readModel, "hooks");
  const impactEvidence = readModel.registryControl?.some((row) => row.id === "capabilities") ?? false;
  const baseEvidence = [`revision:${readModel.registryRevision.revision ?? "unavailable"}`, `snapshot:${readModel.snapshotHash}`];
  return [
    { id: "forgejo-brain-read", label: "Brain-Datei aus Forgejo live sehen", status: brainReady ? "ready" : "gap", section: "Explorer/Wissen", evidence: [...baseEvidence, `brainEntries:${readModel.brainEntries?.length ?? 0}`] },
    { id: "obsidian-dirty-diff", label: "Obsidian-Änderung erkennen und Working-Copy-Diff öffnen", status: brainReady ? "owner_test_required" : "gap", section: "Explorer", evidence: [...baseEvidence, "Explorer/Diff-Flow owner-facing"] },
    { id: "brain-metadata-edit", label: "Tag/Kategorie ändern, freigeben, Forgejo-Commit und Runtime-Sync beobachten", status: brainMutable ? "owner_test_required" : "gap", section: "Wissen", evidence: [...baseEvidence, `mutableBrainEntries:${(readModel.brainEntries ?? []).filter((entry) => entry.mutationSupported === true || typeof entry.mutationBaseHash === "string").length}`] },
    { id: "brain-rollback", label: "Lesson rollbacken und im nächsten Run nicht mehr injizieren", status: impactEvidence ? "owner_test_required" : "gap", section: "Wissen", evidence: [...baseEvidence, "Impact & Injection Graph"] },
    { id: "skill-create", label: "Skill erstellen oder ändern", status: skillsReady ? "owner_test_required" : "gap", section: "Skills", evidence: [...baseEvidence, `skillsUnifiedLane:${skillsReady}`] },
    { id: "routine-edit", label: "Routine bearbeiten", status: routinesReady ? "owner_test_required" : "gap", section: "Routinen", evidence: [...baseEvidence, `routinesUnifiedLane:${routinesReady}`] },
    { id: "policy-tighten", label: "Policy einschränken und negativen Test sehen", status: policiesReady ? "owner_test_required" : "gap", section: "Policies", evidence: [...baseEvidence, `policiesUnifiedLane:${policiesReady}`] },
    { id: "draft-delete-deprecate-purge", label: "Draft löschen, versioniertes Objekt deprecaten, Purge-Impact prüfen", status: brainMutable && impactEvidence ? "owner_test_required" : "gap", section: "Wissen/Expert", evidence: [...baseEvidence, "controlled editor + impact graph"] },
    { id: "conflict-resolution", label: "Konflikt ohne Lost Update lösen", status: brainReady ? "owner_test_required" : "gap", section: "Explorer/Change Lane", evidence: [...baseEvidence, "If-Match/409 Conflict Lane"] },
    { id: "hook-sync", label: "Hook klassifizieren und auf Host synchronisieren", status: hooksStatus === "live_control_ready" ? "owner_test_required" : "gap", section: "Hooks/Hosts", evidence: [...baseEvidence, `hooks:${hooksStatus ?? "unavailable"}`] },
  ];
}

function statusLabel(status: OwnerAcceptanceStatus): string {
  if (status === "ready") return "bereit";
  if (status === "owner_test_required") return "Owner-Test";
  return "Gap";
}

export function AgentOsOwnerAcceptancePanel({ readModel }: { readModel: AgentOsStudioReadModelView }): JSX.Element {
  const rows = deriveOwnerAcceptanceJourneys(readModel);
  const ready = rows.filter((row) => row.status === "ready" || row.status === "owner_test_required").length;
  const gaps = rows.filter((row) => row.status === "gap").length;
  return (
    <section className="mb-4 rounded-lg border border-white/10 bg-black/15 p-4" aria-label="Owner UI Acceptance">
      <div className="flex min-w-0 flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.14em] text-jarvis-accent">Owner UI Acceptance</p>
          <h3 className="mt-1 text-sm font-semibold text-jarvis-text">Abnahme-Journeys mit Evidence statt Bauchgefühl</h3>
          <p className="mt-1 max-w-3xl text-xs leading-relaxed text-jarvis-muted">
            Diese Liste zeigt, welche finalen Owner-Wege im UI vorhanden sind, welche du nach Login real ausführen musst und wo noch bewusst kein grünes GO behauptet wird.
          </p>
        </div>
        <div className="flex gap-1.5 text-[10px]">
          <span className="rounded-md border border-jarvis-accent/25 bg-jarvis-accent/10 px-2 py-1 text-jarvis-text">{ready} abnahmebereit</span>
          <span className="rounded-md border border-amber-300/25 bg-amber-300/10 px-2 py-1 text-amber-100">{gaps} Gap</span>
        </div>
      </div>
      <ul className="mt-3 grid gap-2 lg:grid-cols-2">
        {rows.map((row) => (
          <li key={row.id} className="rounded-lg border border-white/10 bg-black/20 p-3">
            <div className="flex min-w-0 items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-xs font-medium text-jarvis-text">{row.label}</p>
                <p className="mt-1 text-[10px] text-jarvis-muted">{row.section}</p>
              </div>
              <StatusBadge status={row.status === "gap" ? "blocked" : row.status === "owner_test_required" ? "awaiting_approval" : "ready"} label={statusLabel(row.status)} />
            </div>
            <p className="mt-2 break-all font-mono text-[10px] text-jarvis-muted">{row.evidence.join(" · ")}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}
